/**
 * Hook del wallet de la Home: carga los movimientos reales combinando sesiones
 * (consumos) + ledger (recargas/bonos) y los unifica con `buildWalletTxns`.
 *
 * Lazy: NO fetchea hasta que `enabled` pasa a true (cuando el usuario abre la
 * hoja de saldo) — así la Home no paga requests extra en el render inicial (el
 * saldo del pill ya viene por `useCredits`). Refetchea en **cada apertura** y
 * **en vivo** cuando cambia el saldo (evento global `orborix:credits.refresh`,
 * el mismo que refresca el pill vía `useCredits`): así un movimiento nuevo —p.ej.
 * tras comprar orbs con la hoja de saldo abierta— aparece SIN tener que cerrar y
 * reabrir la wallet.
 */
import { useEffect, useRef, useState } from 'react';
import { listSessions } from '../lib/sessionsApi.js';
import { fetchLedgerTransactions, fetchReviewDays } from './walletApi.js';
import { buildWalletTxns, type WalletTxn } from './walletModel.js';

const SESSIONS_LIMIT = 30;
const LEDGER_LIMIT = 50;

export interface UseWalletResult {
  txns: WalletTxn[];
  loading: boolean;
  error: Error | null;
}

export function useWallet(enabled: boolean): UseWalletResult {
  const [txns, setTxns] = useState<WalletTxn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Cada carga usa su propio AbortController y cancela la anterior, para que un
    // refresh disparado por una compra no compita con el fetch inicial en vuelo
    // (evita que una respuesta vieja pise a la nueva).
    const load = (): void => {
      ctrlRef.current?.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;
      setLoading(true);
      setError(null);

      Promise.all([
        listSessions({ limit: SESSIONS_LIMIT }, ctrl.signal),
        fetchLedgerTransactions(LEDGER_LIMIT, ctrl.signal).catch(() => []),
        fetchReviewDays(ctrl.signal).catch((err: unknown) => {
          console.warn('[wallet] review days failed', err);
          return [];
        }),
      ])
        .then(([page, ledger, reviewDays]) => {
          if (ctrl.signal.aborted) return;
          setTxns(buildWalletTxns(page.items, ledger, reviewDays));
        })
        .catch((err: unknown) => {
          if (ctrl.signal.aborted) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setError(err instanceof Error ? err : new Error(String(err)));
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLoading(false);
        });
    };

    // Fetch al abrir la hoja + recarga cuando el saldo cambia. `useCredits`
    // escucha el mismo evento para el pill; acá lo hacemos para la lista.
    load();
    const onCreditsRefresh = (): void => load();
    window.addEventListener('orborix:credits.refresh', onCreditsRefresh);

    return () => {
      window.removeEventListener('orborix:credits.refresh', onCreditsRefresh);
      ctrlRef.current?.abort();
    };
  }, [enabled]);

  return { txns, loading, error };
}
